import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Download, Loader2, Check, X, Trash2, ChevronUp, ChevronDown, Cloud } from 'lucide-react';
import { useBackgroundDownload } from '@/hooks/useBackgroundDownload';
import { formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const FloatingDownloadIndicator = () => {
  const { activeDownloads, downloadCompletedZip, deleteDownloadJob } = useBackgroundDownload();
  const [expanded, setExpanded] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Only show if there are active downloads
  const visibleDownloads = activeDownloads.filter(d => 
    d.status === 'pending' || d.status === 'processing' || d.status === 'completed'
  );

  // Detect stale processing jobs (no progress for 5+ min)
  const staleJobs = visibleDownloads.filter(d => {
    if (d.status !== 'processing' && d.status !== 'pending') return false;
    const updatedAt = new Date(d.created_at).getTime();
    const now = Date.now();
    return (now - updatedAt) > 5 * 60 * 1000 && d.progress < 95;
  });

  if (visibleDownloads.length === 0) return null;

  const inProgress = visibleDownloads.filter(d => d.status === 'pending' || d.status === 'processing');
  const completed = visibleDownloads.filter(d => d.status === 'completed');

  const handleDownload = async (job: typeof activeDownloads[0]) => {
    setDownloadingId(job.id);
    await downloadCompletedZip(job);
    setDownloadingId(null);
  };

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border bg-background shadow-lg"
    >
      {/* Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 rounded-t-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <Cloud className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">
            {inProgress.length > 0 
              ? `Downloading ${inProgress.length} job${inProgress.length > 1 ? 's' : ''}...`
              : `${completed.length} download${completed.length > 1 ? 's' : ''} ready`
            }
          </span>
        </div>
        <div className="flex items-center gap-2">
          {inProgress.length > 0 && (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          )}
          {completed.length > 0 && (
            <Badge variant="secondary" className="bg-green-500/20 text-green-500 text-xs px-1.5">
              {completed.length}
            </Badge>
          )}
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </div>
      </button>

      {/* Mini progress bar when collapsed */}
      {!expanded && inProgress.length > 0 && (
        <div className="px-3 pb-2">
          <Progress value={inProgress[0].progress} className="h-1.5" />
          <p className="text-[10px] text-muted-foreground mt-1 text-right">
            {inProgress[0].processed_images}/{inProgress[0].total_images} images • {inProgress[0].progress}%
          </p>
        </div>
      )}

      {/* Expanded list */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t"
          >
            <div className="max-h-60 overflow-y-auto p-2 space-y-2">
              {visibleDownloads.map(job => (
                <div key={job.id} className="p-2.5 rounded-md bg-muted/30 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {job.status === 'processing' && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                        {job.status === 'completed' && <Check className="h-3 w-3 text-green-500" />}
                        {job.status === 'pending' && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                        <span className="text-xs font-medium truncate">
                          {job.job_ids.length} job{job.job_ids.length > 1 ? 's' : ''}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      {job.total_images > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {job.processed_images}/{job.total_images} images
                          {job.file_size_bytes ? ` • ${formatBytes(job.file_size_bytes)}` : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {job.status === 'completed' && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDownload(job)} disabled={downloadingId === job.id}>
                          {downloadingId === job.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteDownloadJob(job.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                  {(job.status === 'pending' || job.status === 'processing') && (
                    <Progress value={job.progress} className="h-1.5" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
